import { useState } from "react";
import type { EnrichedProject } from "../types/index.js";
import { apiPost, apiPut } from "../lib/api.js";
import FolderExplorerModal from "./FolderExplorerModal.js";
import ProjectSettingsPanel from "./ProjectSettingsPanel.js";

interface ProjectListProps {
	projects: EnrichedProject[];
	selectedProjectId: string | null;
	loading: boolean;
	error: string | null;
	onSelect: (id: string | null) => void;
	onFolderLinked: () => void;
	/** Custom message to display in the empty state. Defaults to "No projects found". */
	emptyMessage?: string;
}

/**
 * Sidebar project list.
 *
 * Shows loading state, empty state, error state, or the list of enriched projects.
 * The selected project receives an orange left border highlight.
 * Each project shows an ingestion badge dot indicating document indexing status:
 * - Green (ready): documents have been indexed and are available for retrieval
 * - Amber (pending): indexing is in progress or queued
 * - None: no documents indexed yet
 *
 * Projects without a linked WorkDrive folder show a folder icon button that
 * opens the FolderExplorerModal for browsing and selecting a folder.
 * Projects with a linked folder show a cog icon that opens the ProjectSettingsPanel.
 */
export default function ProjectList({
	projects,
	selectedProjectId,
	loading,
	error,
	onSelect,
	onFolderLinked,
	emptyMessage = "No projects found",
}: ProjectListProps) {
	/** ID of the project whose folder-explorer modal is currently open, or null. */
	const [explorerProjectId, setExplorerProjectId] = useState<string | null>(null);

	/** ID of the project whose settings panel is open, or null. */
	const [settingsProjectId, setSettingsProjectId] = useState<string | null>(null);

	/** Bounding rect of the cog button that triggered the settings panel. */
	const [settingsAnchorRect, setSettingsAnchorRect] = useState<DOMRect | null>(null);

	if (loading) {
		return (
			<div className="project-list">
				<div className="project-list-loading">
					<div className="loading-spinner" />
					<span>Loading projects...</span>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="project-list">
				<div className="project-list-error">{error}</div>
			</div>
		);
	}

	if (projects.length === 0) {
		return (
			<div className="project-list">
				<div className="project-list-empty">{emptyMessage}</div>
			</div>
		);
	}

	/** The project whose explorer is currently open (if any). */
	const explorerProject = explorerProjectId
		? projects.find((p) => p.id === explorerProjectId) ?? null
		: null;

	/**
	 * Called when the user confirms a folder selection in the explorer modal.
	 * Registers the project in Supabase if not yet registered, then links the folder.
	 */
	async function handleFolderSelected(folderId: string): Promise<void> {
		if (!explorerProject) return;

		try {
			/* Register project in Supabase first if it has no supabaseId yet */
			if (explorerProject.supabaseId === null) {
				const registerResponse = await apiPost(
					`/api/projects/${explorerProject.id}/register`,
					{ name: explorerProject.name },
				);
				if (!registerResponse.ok) {
					const errorData = await registerResponse
						.json()
						.catch(() => null) as { message?: string } | null;
					window.alert(
						errorData?.message ??
							`Registration failed (${registerResponse.status})`,
					);
					return;
				}
			}

			/* Link the selected folder */
			const linkResponse = await apiPut(
				`/api/projects/${explorerProject.id}/folder`,
				{ folderId },
			);
			if (!linkResponse.ok) {
				const errorData = await linkResponse
					.json()
					.catch(() => null) as { message?: string } | null;
				window.alert(
					errorData?.message ??
						`Failed to link folder (${linkResponse.status})`,
				);
				return;
			}

			setExplorerProjectId(null);
			onFolderLinked();
		} catch {
			window.alert("Failed to connect to the server. Please try again.");
		}
	}

	/** Closes the settings panel and clears its state. */
	function closeSettings(): void {
		setSettingsProjectId(null);
		setSettingsAnchorRect(null);
	}

	/** Closes the settings panel and triggers a project list refresh. */
	function handleSettingsAction(): void {
		closeSettings();
		onFolderLinked();
	}

	/** The project whose settings panel is currently open (if any). */
	const settingsProject = settingsProjectId
		? projects.find((p) => p.id === settingsProjectId) ?? null
		: null;

	return (
		<>
			<div className="project-list">
				{projects.map((project) => (
					<div key={project.id} className="project-row">
						{/* Project button + folder action trigger */}
						<div className="project-item-wrapper">
							<button
								className={`project-item${selectedProjectId === project.id ? " project-item--selected" : ""}`}
								onClick={() => onSelect(project.id)}
								type="button"
								title={project.description || project.name}
							>
								<span className="project-item-header">
									<span className="project-item-name">{project.name}</span>
									{project.ingestionBadge !== "none" && (
										<span
											className={`project-badge project-badge--${project.ingestionBadge}`}
											title={
												project.ingestionBadge === "ready"
													? "Documents indexed"
													: "Indexing in progress"
											}
											aria-label={
												project.ingestionBadge === "ready"
													? "Documents indexed"
													: "Indexing in progress"
											}
										/>
									)}
								</span>
								<span className="project-item-status">{project.status}</span>
							</button>

							{/* Folder link button — only shown when no folder is linked */}
							{project.workdriveFolderId === null && (
								<button
									type="button"
									className="project-link-folder-btn"
									title="Link a WorkDrive folder to this project"
									aria-label={`Link WorkDrive folder to ${project.name}`}
									onClick={(e) => {
										e.stopPropagation();
										setExplorerProjectId(project.id);
									}}
								>
									&#128193;
								</button>
							)}

							{/* Settings cog — shown when a folder is already linked */}
							{project.workdriveFolderId !== null && (
								<button
									type="button"
									className={`project-settings-btn${settingsProjectId === project.id ? " project-settings-btn--active" : ""}`}
									title="Project settings"
									aria-label={`Settings for ${project.name}`}
									onClick={(e) => {
										e.stopPropagation();
										const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
										if (settingsProjectId === project.id) {
											closeSettings();
										} else {
											setSettingsProjectId(project.id);
											setSettingsAnchorRect(rect);
										}
									}}
								>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
										<circle cx="12" cy="12" r="3"/>
										<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
									</svg>
								</button>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Folder explorer modal -- rendered via portal when a project's folder button is clicked */}
			{explorerProjectId !== null && (
				<FolderExplorerModal
					onSelect={(folderId) => void handleFolderSelected(folderId)}
					onClose={() => setExplorerProjectId(null)}
				/>
			)}

			{/* Project settings panel -- rendered via portal when the cog button is clicked */}
			{settingsProject !== null && settingsAnchorRect !== null && (
				<ProjectSettingsPanel
					project={settingsProject}
					anchorRect={settingsAnchorRect}
					onClose={closeSettings}
					onSyncTriggered={handleSettingsAction}
					onFolderChanged={handleSettingsAction}
					onFolderUnlinked={handleSettingsAction}
					lastIngestedAt={settingsProject.lastIngestedAt}
				/>
			)}
		</>
	);
}
